import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Grid,
  Heading,
  HStack,
  Image,
  Spacer,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import "@fontsource/work-sans/400.css";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import {
  getActualValues,
  getBalanceSheetOverview,
  getCashMovement,
  getForecasts,
  getYtdAverages,
} from "../../api/detail";
import { getPlValues, getResourcePerformance } from "../../api/growthPlan";
import { getMetricData, getMetricGraphData } from "../../api/metric";
import {
  colorExpenseFormat,
  colorFormat,
  customCurrencyFormat,
} from "../../utils";
import CalendarComponent from "../dashboard/calendarComponent";
import {
  BalanceSheet,
  DetailRow,
  LowerDetailRow,
  ProfitLoss,
} from "../dashboard/detailView";
import ProfitCard from "../dashboard/profitCard";
import PDFMetricGraph from "./reportComponents/pdfMetricGraph";
import ReportCard from "./reportComponents/reportCard";
import ReportExpenseCard from "./reportComponents/reportExpenseCard";

const chunkCashDataNested = (data, rowsPerPage = 18) => {
  if (!data) return [];

  const chunks = [];
  let currentChunk = {};
  let rowCount = 0;

  Object.keys(data).forEach((outerKey) => {
    const innerEntries = Object.entries(data[outerKey]);
    innerEntries.forEach(([innerKey, value]) => {
      if (!currentChunk[outerKey]) currentChunk[outerKey] = {};
      currentChunk[outerKey][innerKey] = value;
      rowCount++;

      if (rowCount >= rowsPerPage) {
        chunks.push(currentChunk);
        currentChunk = {};
        rowCount = 0;
      }
    });
  });

  if (Object.keys(currentChunk).length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};

export const PdfContent = ({
  customer,
  hideButtons = false,
  externalEndDate,
  externalPrintRef,
  trial,
}) => {
  const [trigger, triggerSetter] = useState(true);
  const [activeRow, setActiveRow] = useState(null);
  const customer_id = customer?.id;
  const now = new Date();
  const firstDayOfCurrentMonthUTC = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)
  );

  const lastDayOfPreviousMonth = new Date(firstDayOfCurrentMonthUTC);
  lastDayOfPreviousMonth.setUTCDate(0);

  const [dashDate, setDashDate] = useState(lastDayOfPreviousMonth);

  const formattedDate = format(externalEndDate || dashDate, "yyyy-MM-dd");

  const [metricData, setMetricData] = useState({
    income: "0.00",
    income_forecast: "0.00",
    income_forecast_percentage: "0.00",
    expense: "0.00",
    expense_forecast: "0.00",
    expense_forecast_percentage: "0.00",
    profit: "0.00",
    cogs: "0.00",
    cogs_percentage: "0.00",
    marketing: "0.00",
    marketing_percentage: "0.00",
    team: "0.00",
    team_percentage: "0.00",
    overhead: "0.00",
    overhead_percentage: "0.00",
    profit_percentage: "0.00",
    ytd_income: "0.00",
    ytd_expense: "0.00",
    ytd_income_percentage: "0.00",
    ytd_expense_percentage: "0.00",
  });

  const [pLData, setPLData] = useState({
    overhead_pl: "0.00",
    team_pl: "0.00",
    profit_pl: "0.00",
    marketing_pl: "0.00",
    cogs_pl: "0.00",
  });

  const [ytdData, setYtdData] = useState({
    income: "0.00",
    cogs: "0.00",
    gross_profit: "0.00",
    marketing: "0.00",
    team: "0.00",
    practice_costs: "0.00",
    overhead: "0.00",
    business_development: "0.00",
    expense: "0.00",
    profit: "0.00",
    profit_percentage: "0.00",
  });

  const [actualData, setActualData] = useState({
    income: "0.00",
    cogs: "0.00",
    gross_profit: "0.00",
    marketing: "0.00",
    team: "0.00",
    practice_costs: "0.00",
    overhead: "0.00",
    business_development: "0.00",
    expense: "0.00",
    profit: "0.00",
    profit_percentage: "0.00",
  });

  const [forecastData, setForecastData] = useState({
    income: "0.00",
    cogs: "0.00",
    gross_profit: "0.00",
    marketing: "0.00",
    team: "0.00",
    practice_costs: "0.00",
    overhead: "0.00",
    business_development: "0.00",
    expense: "0.00",
    profit: "0.00",
    profit_percentage: "0.00",
  });

  const [cashData, setCashData] = useState({
    "Bank Accounts": {},
    "Credit Cards": {},
    "Total Movement": {},
  });

  const [balanceSheetData, setBalanceSheetData] = useState({
    acc_recievable: "0.00",
    last_acc_recievable: "0.00",
    bank_accounts: "0.00",
    last_bank_accounts: "0.00",
    acc_payable: "0.00",
    last_acc_payable: "0.00",
    credit_cards: "0.00",
    last_credit_cards: "0.00",
    fixed_assets: "0.00",
    last_fixed_assets: "0.00",
    current_assets: "0.00",
    last_current_assets: "0.00",
    long_term_liabilities: "0.00",
    last_long_term_liabilities: "0.00",
    current_liabilities: "0.00",
    last_current_liabilities: "0.00",
    equity: "0.00",
    last_equity: "0.00",

    acc_recievable_movement: "0.00",
    bank_accounts_movement: "0.00",
    acc_payable_movement: "0.00",
    credit_cards_movement: "0.00",
    fixed_assets_movement: "0.00",
    current_assets_movement: "0.00",
    long_term_liabilities_movement: "0.00",
    current_liabilities_movement: "0.00",
    equity_movement: "0.00",
  });
  const [splitToNewPage, setSplitToNewPage] = useState(false);
  const measurementRef = useRef(null);
  const [hasXOverflow, setHasXOverflow] = useState(false);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [hasCashXOverflow, setHasCashXOverflow] = useState(false);
  const [scrollCashWidth, setScrollCashWidth] = useState(0);
  const [hasXBalanceOverflow, setHasBalanceXOverflow] = useState(false);
  const [scrollBalanceWidth, setScrollBalanceWidth] = useState(0);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const contentCashRef = useRef(null);
  const scrollContainerCashRef = useRef(null);
  const contentBalanceRef = useRef(null);
  const scrollContainerBalanceRef = useRef(null);
  const [metricGraphData, setMetricGraphData] = useState({
    income: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    expense: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    profit: [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0],
    months: ["-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-", "-"],
  });
  const [resourceOverview, setResourceOverview] = useState({
    first_recommended_resource: null,
    second_recommended_resource: null,
    third_recommended_resource: null,
    performance_overview: null,
  });
  const [ytd, setYtd] = useState(
    JSON.parse(sessionStorage.getItem("ytd") || "false")
  );

  const [customTarget, setCustomTarget] = useState(
        JSON.parse(localStorage.getItem("isCustomTarget") || "false")
      );

      useEffect(() => {
        const handleTargetTypeChange = (event) => {
          setCustomTarget(event.detail);
        };

        window.addEventListener("targetTypeChanged", handleTargetTypeChange);

        return () => {
          window.removeEventListener("targetTypeChanged", handleTargetTypeChange);
        };
      }, []);

  const reportDate = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  })
    .format(externalEndDate ? externalEndDate : dashDate)
    .toUpperCase();

  const reportShortDate = externalEndDate
    ? externalEndDate
        .toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        })
        .toUpperCase()
    : dashDate
        .toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        })
        .toUpperCase();

  useEffect(() => {
    if (measurementRef.current) {
      const totalHeight = measurementRef.current.offsetHeight;
      if (totalHeight > 800) {
        setSplitToNewPage(true);
      } else {
        setSplitToNewPage(false);
      }
    }
  }, [cashData, balanceSheetData]);

  useEffect(() => {
    const handler = () => {
      const ytd = JSON.parse(sessionStorage.getItem("ytd") || "false");
      setYtd(ytd);
    };

    window.addEventListener("ytd-changed", handler);
    return () => window.removeEventListener("ytd-changed", handler);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    // getMetricGraphData always uses ytd:false so the financial trend
    // chart always displays 12 months regardless of the ytd toggle.
    const endpoints = [
      { fn: getMetricData, setter: setMetricData, params: { ytd, customTarget } },
      { fn: getMetricGraphData, setter: setMetricGraphData, params: { ytd: false, customTarget } },
      { fn: getPlValues, setter: setPLData, params: { ytd, customTarget } },
      { fn: getActualValues, setter: setActualData, params: { ytd, customTarget } },
      { fn: getYtdAverages, setter: setYtdData, params: { ytd, customTarget } },
      { fn: getForecasts, setter: setForecastData, params: { ytd, customTarget } },
      { fn: getCashMovement, setter: setCashData, params: { ytd, customTarget } },
      { fn: getBalanceSheetOverview, setter: setBalanceSheetData, params: { ytd, customTarget } },
    ];

    const fetchAll = async () => {
      if (!customer_id) return;
      setLoading(true);
      try {
        await Promise.all(
          endpoints.map(async ({ fn, setter, params }) => {
            try {
              const res = await fn(
                customer_id,
                formattedDate,
                controller.signal,
                params
              );
              if (isActive) setter(res.data);
            } catch (err) {
              if (
                err?.name !== "CanceledError" &&
                err?.code !== "ERR_CANCELED"
              ) {
                console.error(`Error in ${fn.name}:`, err);
              }
            }
          })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    triggerSetter(false);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, trigger, triggerSetter, formattedDate, setLoading, ytd, customTarget]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && scrollContainerRef.current) {
        const hasOverflowX =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setHasXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentRef.current.scrollWidth;
          const visibleWidth = scrollContainerRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollWidth(thumbWidth);
        } else {
          setScrollWidth(0);
        }
      }
    };

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("resize", checkOverflow);

    checkOverflow();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [formattedDate]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && scrollContainerRef.current) {
        const hasOverflowX =
          contentRef.current.scrollWidth > contentRef.current.clientWidth;
        setHasXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentRef.current.scrollWidth;
          const visibleWidth = scrollContainerRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollWidth(thumbWidth);
        } else {
          setScrollWidth(0);
        }
      }
    };

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentRef.current) {
      observer.observe(contentRef.current, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("resize", checkOverflow);

    checkOverflow();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [formattedDate]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentCashRef.current && scrollContainerCashRef.current) {
        const hasOverflowX =
          contentCashRef.current.scrollWidth >
          contentCashRef.current.clientWidth;
        setHasCashXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentCashRef.current.scrollWidth;
          const visibleWidth = scrollContainerCashRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollCashWidth(thumbWidth);
        } else {
          setScrollCashWidth(0);
        }
      }
    };

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentCashRef.current) {
      observer.observe(contentCashRef.current, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("resize", checkOverflow);

    checkOverflow();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [formattedDate]);

  useEffect(() => {
    const checkOverflow = () => {
      if (contentBalanceRef.current && scrollContainerBalanceRef.current) {
        const hasOverflowX =
          contentBalanceRef.current.scrollWidth >
          contentBalanceRef.current.clientWidth;
        setHasBalanceXOverflow(hasOverflowX);

        if (hasOverflowX) {
          const contentWidth = contentBalanceRef.current.scrollWidth;
          const visibleWidth = scrollContainerBalanceRef.current.clientWidth;

          const thumbWidth = (visibleWidth / contentWidth) * 100;
          setScrollBalanceWidth(thumbWidth);
        } else {
          setScrollBalanceWidth(0);
        }
      }
    };

    const observer = new MutationObserver(() => {
      checkOverflow();
    });

    if (contentBalanceRef.current) {
      observer.observe(contentBalanceRef.current, {
        childList: true,
        subtree: true,
      });
    }

    window.addEventListener("resize", checkOverflow);

    checkOverflow();

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [formattedDate]);

  useEffect(() => {
    const controller = new AbortController();
    let isActive = true;

    const refreshResourceOverview = async (id) => {
      if (id) {
        try {
          const res = await getResourcePerformance(
            id,
            formattedDate,
            controller.signal,
            { ytd }
          );
          if (isActive) {
            setResourceOverview(res.data);
          }
        } catch (err) {
          if (err?.response) {
            console.log(err);
          }
        }
      }
    };

    refreshResourceOverview(customer_id);

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [customer_id, trigger, triggerSetter, formattedDate, ytd]);

  useEffect(() => {
    setActiveRow(null);
  }, [customer_id, formattedDate]);

  const printRef = useRef(null);

  const handlePrint = useReactToPrint({
    contentRef: externalPrintRef ? externalPrintRef : printRef,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 0;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
        .no-print {
          display: none !important;
        }
      }
    `,
    documentTitle: `Lucro Performance Review ${reportShortDate}`,
  });

  const cashRef = useRef(null);
  const balanceRef = useRef(null);
  const [fitsTogether, setFitsTogether] = useState(false);

  useEffect(() => {
    if (cashRef.current && balanceRef.current) {
      const cashHeight = cashRef.current.offsetHeight || 0;
      const balanceHeight = balanceRef.current.offsetHeight || 0;

      setFitsTogether(cashHeight + balanceHeight <= 850);
    }
  }, [cashData, balanceSheetData]);

  let currentPage = 4;

  return (
    <Box width={"auto"} overflowX={"auto"} overflowY={"auto"}>
      {!hideButtons && (
        <HStack>
          <Box width={"100%"} align={"left"} mb={"20px"}>
            <CalendarComponent
              height="36px"
              callbackFunctionDate={setDashDate}
              date={dashDate}
            />
          </Box>
          <Box width={"100%"} align={"right"} mb={"20px"}>
            {!loading && (
              <Button onClick={handlePrint}>Print / Save as PDF</Button>
            )}
          </Box>
        </HStack>
      )}
      <Box
        id="pdf-content"
        ref={externalPrintRef ? externalPrintRef : printRef}
        width="794px"
        color="black"
        fontSize="sm"
        fontFamily="'Work Sans', sans-serif"
        backgroundColor={"transparent"}
      >
        <Box
          h="1122px"
          bg="#1a202c"
          color="white"
          p="0px 80px"
          backgroundImage="url(/reportBackground.png)"
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          display="flex"
          flexDirection="column"
        >
          <Box
            flex="1"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Heading
              fontSize="70px"
              fontWeight="bold"
              lineHeight="1.1"
              mb="25px"
              wordBreak="break-word"
              whiteSpace="normal"
            >
              {customer?.name?.toUpperCase()}
            </Heading>
            <Text
              fontSize="30px"
              fontWeight="bold"
              lineHeight="1.1"
              color="#f6e05e"
              fontStyle={"italic"}
            >
              Bookkeeping Made Easy, Profits Made Real.
            </Text>
          </Box>

          <HStack width="100%" mt="auto" mb="40px">
            <Text fontSize="20px" textAlign="left">
              MONTHLY REPORT
            </Text>
            <Box width="60px" border="1px solid white" m={"0px 15px"} />
            <Text fontSize="20px" textAlign="right">
              {reportDate}
            </Text>
          </HStack>
        </Box>

        <Box className="no-print" m={"20px"}></Box>

        <Box
          h="1122px"
          style={{ pageBreakBefore: "always" }}
          p={"40px 40px 0px"}
          backgroundColor={"white"}
        >
          <Flex direction="column" h="100%">
            <Flex
              flexDir={"row"}
              borderBottom={"6px solid black"}
              pb={"15px"}
              mb={"40px"}
            >
              <Heading fontSize={"27px"}>METRICS OVERVIEW</Heading>
              <Spacer />
              <Heading fontSize={"27px"} textAlign={"right"} width={"40%"}>
                {reportDate}
              </Heading>
            </Flex>
            <Grid
              templateColumns="2fr 225px 2fr"
              spacing={2}
              p="10px 0px 0px 0px"
              alignItems="center"
              justifyItems="center"
            >
              <ReportCard
                title="Income"
                amount={customCurrencyFormat(
                  metricData ? metricData.income : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_income : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_income_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.income_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.income_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.income_forecast_percentage : "0.00"
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_income_percentage : "0.00"
                )}
              />
              <Stack align="center" width="188px">
                <ProfitCard
                  profit={customCurrencyFormat(
                    metricData ? metricData.profit_percentage : 0.0,
                    true
                  )}
                  pdfFormat={true}
                  profitPercentage={customCurrencyFormat(
                    metricData ? metricData.profit : 0.0
                  )}
                  perfectPL={customCurrencyFormat(
                    pLData ? pLData.profit_pl : 0.0,
                    true
                  )}
                  color={colorExpenseFormat(
                    pLData ? pLData.profit_pl : "0.00",
                    metricData ? metricData.profit_percentage : "0.00",
                    true,
                    true
                  )}
                  dimensions={"188px"}
                  circleDimensions={"220px"}
                />
              </Stack>
              <ReportCard
                title="Total Expenses"
                amount={customCurrencyFormat(
                  metricData ? metricData.expense : 0.0
                )}
                ytd={customCurrencyFormat(
                  metricData ? metricData.ytd_expense : 0.0
                )}
                ytd_percentage={customCurrencyFormat(
                  metricData ? metricData.ytd_expense_percentage : 0.0,
                  true
                )}
                forecast={customCurrencyFormat(
                  metricData ? metricData.expense_forecast : 0.0
                )}
                forecast_percentage={customCurrencyFormat(
                  metricData ? metricData.expense_forecast_percentage : 0.0,
                  true
                )}
                forecast_color={colorFormat(
                  metricData ? metricData.expense_forecast_percentage : "0.00",
                  true
                )}
                ytd_color={colorFormat(
                  metricData ? metricData.ytd_expense_percentage : "0.00",
                  true
                )}
              />
            </Grid>
            <Flex direction="row" gap="15px" fontFamily="'Lato', sans-serif">
              <Card
                boxShadow="none"
                mt={"15px"}
                outline={"1px solid #CBD5E0"}
                height="320px"
                maxHeight={"100%"}
                width="100%"
                borderRadius={"12px"}
                marginBottom={"15px"}
              >
                <CardBody
                  boxShadow="none"
                  height="30%"
                  p={"20px 24px 20px 24px "}
                >
                  <PDFMetricGraph metricGraphData={metricGraphData} />
                </CardBody>
              </Card>
            </Flex>

            <Flex
              direction="row"
              gap="15px"
              mb={"15px"}
              fontFamily="'Lato', sans-serif"
            >
              <ReportExpenseCard
                title="Team"
                total={customCurrencyFormat(metricData ? metricData.team : 0.0)}
                percentage={customCurrencyFormat(
                  metricData ? metricData.team_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.team_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.team_pl : "0.00",
                  metricData ? metricData.team_percentage : "0.00"
                )}
              ></ReportExpenseCard>
              <ReportExpenseCard
                title="Marketing"
                total={customCurrencyFormat(
                  metricData ? metricData.marketing : 0.0
                )}
                percentage={customCurrencyFormat(
                  metricData ? metricData.marketing_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.marketing_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.marketing_pl : "0.00",
                  metricData ? metricData.marketing_percentage : "0.00"
                )}
              ></ReportExpenseCard>
            </Flex>
            <Flex direction="row" gap="15px" fontFamily="'Lato', sans-serif">
              <ReportExpenseCard
                title="Operations"
                total={customCurrencyFormat(
                  metricData ? metricData.overhead : 0.0
                )}
                percentage={customCurrencyFormat(
                  metricData ? metricData.overhead_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.overhead_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.overhead_pl : "0.00",
                  metricData ? metricData.overhead_percentage : "0.00"
                )}
              ></ReportExpenseCard>
              <ReportExpenseCard
                title="Products"
                total={customCurrencyFormat(metricData ? metricData.cogs : 0.0)}
                percentage={customCurrencyFormat(
                  metricData ? metricData.cogs_percentage : 0.0,
                  true,
                  false,
                  true,
                  true
                )}
                perfectPL={customCurrencyFormat(
                  pLData ? pLData.cogs_pl : 0.0,
                  true
                )}
                color={colorExpenseFormat(
                  pLData ? pLData.cogs_pl : "0.00",
                  metricData ? metricData.cogs_percentage : "0.00"
                )}
              ></ReportExpenseCard>
            </Flex>
            <Flex
              mt="auto"
              justify="space-between"
              align="center"
              alignItems={"center"}
            >
              <Image
                boxSize="120px"
                objectFit="contain"
                src="../lucro-logo-light.png"
              />
              <HStack>
                <Text
                  display={"flex"}
                  direction={"row"}
                  align={"center"}
                  alignItems={"center"}
                  fontSize="18px"
                >
                  {customer?.name?.toUpperCase()}
                </Text>
                <Box
                  width={"1px"}
                  h={"21px"}
                  border={"1px solid black"}
                  m={"0px 6px"}
                />{" "}
                <Text
                  display={"flex"}
                  direction={"row"}
                  align={"center"}
                  alignItems={"center"}
                  fontSize="18px"
                >
                  PAGE 2
                </Text>
              </HStack>
            </Flex>
          </Flex>
        </Box>
        <Box className="no-print" m={"20px"}></Box>
        <Box
          h="1122px"
          style={{ pageBreakBefore: "always" }}
          p={"40px 40px 0px"}
          backgroundColor={"white"}
        >
          <Flex direction="column" h="100%">
            <Flex
              flexDir={"row"}
              borderBottom={"6px solid black"}
              pb={"15px"}
              mb={"40px"}
            >
              <Heading fontSize={"27px"}>DETAILED OVERVIEW</Heading>
              <Spacer />
              <Heading fontSize={"27px"} textAlign={"right"} width={"40%"}>
                {reportDate}
              </Heading>
            </Flex>

            <Box>
              <ProfitLoss
                ytdAveragesData={ytdData?.averages}
                ytdData={ytdData?.totals}
                customer_id={customer_id}
                formattedDate={formattedDate}
                contentRef={contentRef}
                forecastData={forecastData}
                actualData={actualData}
                setActiveRow={setActiveRow}
                activeRow={activeRow}
                hasXOverflow={hasXOverflow}
                scrollWidth={scrollWidth}
                scrollContainerRef={scrollContainerRef}
                isExport={true}
              />
            </Box>

            <Flex
              mt="auto"
              justify="space-between"
              align="center"
              alignItems={"center"}
              pt="20px"
            >
              <Image
                boxSize="120px"
                objectFit="contain"
                src="../lucro-logo-light.png"
              />

              <HStack>
                <Text
                  display={"flex"}
                  direction={"row"}
                  align={"center"}
                  alignItems={"center"}
                  fontSize="18px"
                >
                  {customer?.name?.toUpperCase()}
                </Text>
                <Box
                  width={"1px"}
                  h={"21px"}
                  border={"1px solid black"}
                  m={"0px 6px"}
                />{" "}
                <Text
                  display={"flex"}
                  direction={"row"}
                  align={"center"}
                  alignItems={"center"}
                  fontSize="18px"
                >
                  PAGE 3
                </Text>
              </HStack>
            </Flex>
          </Flex>
        </Box>
        <Box className="no-print" m={"20px"}></Box>

        {!trial && (
          <>
            {chunkCashDataNested(cashData, 17).map((chunk, pageIdx, arr) => {
              const isLastCashPage = pageIdx === arr.length - 1;

              return (
                <>
                  <Box
                    key={pageIdx}
                    h="1122px"
                    style={{ pageBreakBefore: "always" }}
                    p="40px 40px 0px"
                    backgroundColor="white"
                  >
                    <Flex direction="column" h="100%">
                      <Flex
                        flexDir="row"
                        borderBottom="6px solid black"
                        pb="15px"
                        mb="40px"
                      >
                        <Heading fontSize="27px">DETAILED OVERVIEW</Heading>
                        <Spacer />
                        <Heading fontSize="27px" textAlign="right" width="40%">
                          {reportDate}
                        </Heading>
                      </Flex>

                      <Box
                        height={"fit-content"}
                        ref={isLastCashPage ? cashRef : null}
                      >
                        <Text fontSize="22px" fontWeight={800} mb={4}>
                          Cash Movement
                        </Text>

                        <Box
                          width="100%"
                          bg="white"
                          borderTopRadius="12px"
                          borderBottomRadius="0"
                          p="0"
                          overflow="hidden"
                        >
                          <Table
                            variant="unstyled"
                            sx={{
                              borderCollapse: "separate",
                              borderSpacing: 0,
                              width: "100%",
                              thead: {
                                "tr, th": { border: "none", borderRadius: 0 },
                              },
                              tbody: {
                                tr: {
                                  td: {
                                    borderTop: "1px solid #D1D4DA",
                                    borderLeft: "1px solid #D1D4DA",
                                    borderBottom: "none",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  },
                                  "&:first-of-type td:first-of-type": {
                                    borderLeft: "1px solid #D1D4DA",
                                  },
                                  "&:first-of-type td:last-of-type": {
                                    borderRight: "1px solid #D1D4DA",
                                  },
                                  "&:last-of-type": {
                                    td: { borderBottom: "1px solid #D1D4DA" },
                                    "td:first-of-type": {
                                      borderBottomLeftRadius: "12px",
                                      borderLeft: "1px solid #D1D4DA",
                                    },
                                    "td:last-of-type": {
                                      borderBottomRightRadius: "12px",
                                      borderRight: "1px solid #D1D4DA",
                                    },
                                  },
                                  "td:last-of-type": {
                                    borderRight: "1px solid #D1D4DA",
                                  },
                                },
                              },
                            }}
                          >
                            <Thead backgroundColor="#1A202C">
                              <Tr textColor="white" fontSize="15px">
                                <Td fontWeight={700}>Account</Td>
                                <Td fontWeight={700} whiteSpace={"nowrap"}>
                                  Start Balance
                                </Td>
                                <Td fontWeight={700} whiteSpace={"nowrap"}>
                                  End Balance
                                </Td>
                                <Td fontWeight={700} whiteSpace={"nowrap"}>
                                  Total Movement
                                </Td>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {Object.keys(chunk).map((outerKey) =>
                                Object.entries(chunk[outerKey]).map(
                                  ([innerKey, value], rowIdx) =>
                                    innerKey === "ratio" ? (
                                      <DetailRow
                                        key={`${outerKey}-${innerKey}-${rowIdx}`}
                                        name="Expense Cover Ratio"
                                        forecast={customCurrencyFormat(
                                          value.earliest_ratio,
                                          false,
                                          true,
                                          false
                                        )}
                                        actual={customCurrencyFormat(
                                          value.latest_ratio,
                                          false,
                                          true,
                                          false
                                        )}
                                        ytdEmpty
                                      />
                                    ) : innerKey === "Total" ? (
                                      <LowerDetailRow
                                        key={`${outerKey}-${innerKey}-${rowIdx}`}
                                        name={innerKey}
                                        forecast={customCurrencyFormat(
                                          value.earliest_balance,
                                          false,
                                          true
                                        )}
                                        actual={customCurrencyFormat(
                                          value.latest_balance,
                                          false,
                                          true
                                        )}
                                        ytdAverage={customCurrencyFormat(
                                          value.total_movement,
                                          false,
                                          true
                                        )}
                                      />
                                    ) : (
                                      <DetailRow
                                        key={`${outerKey}-${innerKey}-${rowIdx}`}
                                        name={innerKey}
                                        forecast={customCurrencyFormat(
                                          value.earliest_balance,
                                          false,
                                          true
                                        )}
                                        actual={customCurrencyFormat(
                                          value.latest_balance,
                                          false,
                                          true
                                        )}
                                        ytdAverage={customCurrencyFormat(
                                          value.total_movement,
                                          false,
                                          true
                                        )}
                                      />
                                    )
                                )
                              )}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>

                      {isLastCashPage && fitsTogether && (
                        <Box mt={6} ref={balanceRef}>
                          <BalanceSheet
                            contentBalanceRef={contentBalanceRef}
                            balanceSheetData={balanceSheetData}
                            hasXBalanceOverflow={hasXBalanceOverflow}
                            scrollContainerBalanceRef={
                              scrollContainerBalanceRef
                            }
                            scrollBalanceWidth={scrollBalanceWidth}
                            isExport={true}
                          />
                        </Box>
                      )}

                      <Flex
                        mt="auto"
                        justify="space-between"
                        align="center"
                        pt="20px"
                      >
                        <Image
                          boxSize="120px"
                          objectFit="contain"
                          src="../lucro-logo-light.png"
                        />
                        <HStack>
                          <Text fontSize="18px">
                            {customer?.name?.toUpperCase()}
                          </Text>
                          <Box
                            width="1px"
                            h="21px"
                            border="1px solid black"
                            m="0px 6px"
                          />
                          <Text fontSize="18px">PAGE {currentPage++}</Text>
                        </HStack>
                      </Flex>
                    </Flex>
                  </Box>
                  <Box className="no-print" m={"20px"}></Box>
                </>
              );
            })}

            {!fitsTogether && (
              <Box
                h="1122px"
                style={{ pageBreakBefore: "always" }}
                p="40px 40px 0px"
                backgroundColor="white"
              >
                <Flex direction="column" h="100%">
                  <Flex
                    flexDir="row"
                    borderBottom="6px solid black"
                    pb="15px"
                    mb="40px"
                  >
                    <Heading fontSize="27px">DETAILED OVERVIEW</Heading>
                    <Spacer />
                    <Heading fontSize="27px" textAlign="right" width="40%">
                      {reportDate}
                    </Heading>
                  </Flex>

                  <Box ref={balanceRef}>
                    <BalanceSheet
                      contentBalanceRef={contentBalanceRef}
                      balanceSheetData={balanceSheetData}
                      hasXBalanceOverflow={hasXBalanceOverflow}
                      scrollContainerBalanceRef={scrollContainerBalanceRef}
                      scrollBalanceWidth={scrollBalanceWidth}
                      isExport={true}
                    />
                  </Box>

                  <Flex
                    mt="auto"
                    justify="space-between"
                    align="center"
                    pt="20px"
                  >
                    <Image
                      boxSize="120px"
                      objectFit="contain"
                      src="../lucro-logo-light.png"
                    />
                    <HStack>
                      <Text fontSize="18px">
                        {customer?.name?.toUpperCase()}
                      </Text>
                      <Box
                        width="1px"
                        h="21px"
                        border="1px solid black"
                        m="0px 6px"
                      />
                      <Text fontSize="18px">PAGE {currentPage++}</Text>
                    </HStack>
                  </Flex>
                </Flex>
              </Box>
            )}
          </>
        )}

        <Box className="no-print" m={"20px"}></Box>
        {(resourceOverview?.recommended_resources?.length > 0 ||
          resourceOverview?.performance_overview) && (
          <Box
            h="1122px"
            style={{ pageBreakBefore: "always" }}
            p={"40px 40px 0px"}
            backgroundColor={"white"}
          >
            <Flex direction="column" h="100%">
              <Flex
                flexDir={"row"}
                borderBottom={"6px solid black"}
                pb={"15px"}
                mb={"20px"}
              >
                <Heading fontSize={"27px"}>ACTION STEPS</Heading>
                <Spacer />
                <Heading fontSize={"27px"} textAlign={"right"} width={"40%"}>
                  {reportDate}
                </Heading>
              </Flex>
              {resourceOverview?.performance_overview && (
                <Box mt={"10px"}>
                  <Heading fontSize={"30px"}>Performance Overview</Heading>
                  <Text mt={"10px"}>
                    {resourceOverview?.performance_overview}
                  </Text>
                </Box>
              )}
              {resourceOverview?.recommended_resources?.map(
                (resource, index) => (
                  <Box key={index} mt="10px">
                    <HStack align="center" mt="30px" spacing={4}>
                      <Box
                        height="88px"
                        width="15px"
                        backgroundColor="#536DEC"
                      />
                      <Box pl={"20px"} height={"110px"}>
                        <Heading fontSize="80px">0{index + 1}</Heading>
                      </Box>
                      <VStack align="start" spacing={1} pl={"20px"}>
                        <Heading fontSize="20px">{resource.name}</Heading>
                        <Text fontSize="16px">{resource.description}</Text>
                      </VStack>
                    </HStack>
                  </Box>
                )
              )}
              <Flex
                mt="auto"
                justify="space-between"
                align="center"
                alignItems={"center"}
                pt="20px"
              >
                <Image
                  boxSize="120px"
                  objectFit="contain"
                  src="../lucro-logo-light.png"
                />

                <HStack>
                  <Text
                    display={"flex"}
                    direction={"row"}
                    align={"center"}
                    alignItems={"center"}
                    fontSize="18px"
                  >
                    {customer?.name?.toUpperCase()}
                  </Text>
                  <Box
                    width={"1px"}
                    h={"21px"}
                    border={"1px solid black"}
                    m={"0px 6px"}
                  />{" "}
                  <Text
                    display={"flex"}
                    direction={"row"}
                    align={"center"}
                    alignItems={"center"}
                    fontSize="18px"
                  >
                    PAGE {currentPage++}
                  </Text>
                </HStack>
              </Flex>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};
